import { getBusinessLang, getRequestLang } from '@/utils/requestLang';

interface ILocaleTarget {
  locale: string;
}

interface IMutationSource {
  subscribe(handler: (mutation: { type: string }) => void): unknown;
}

const SET_BUSINESS_INFO = 'business/setBusinessInfo';

/**
 * The copy follows the business's country, as on the host and as the `x-lang`
 * header already does: the merchant has no language switcher. The browser
 * language is deliberately not used here, so a US merchant on a Portuguese
 * browser keeps the English copy; only the pre-auth login flow applies it, through
 * `followPreAuthLocale`.
 */
export const followBusinessLocale = (
  store: IMutationSource,
  i18n: ILocaleTarget
): void => {
  const apply = () => {
    const lang = getBusinessLang();
    if (lang && i18n.locale !== lang) i18n.locale = lang;
  };

  apply();
  store.subscribe(({ type }) => {
    if (type === SET_BUSINESS_INFO) apply();
  });
};

/** The login and the screens reached from it before auth, by route name (`router/root.ts`). */
const PRE_AUTH_ROUTES = ['login', 'Forgot', 'Recovery'];

interface IRouteSource {
  afterEach(hook: (to: { name?: string | null }) => void): unknown;
}

/**
 * The login flow has no business to read a country from, so until one is known
 * it speaks the browser's language (`pt-*` -> pt-BR, else en-US), as the
 * host's public screens and the `x-lang` header already do. It is a property of
 * the flow, not of one screen: moving between the login, forgot password and
 * reset password keeps it, and a direct landing on any of them (the reset link
 * in the email) gets it. Leaving the flow hands the locale back — to the
 * business country if one loaded meanwhile, otherwise to what it was before.
 */
export const followPreAuthLocale = (
  router: IRouteSource,
  i18n: ILocaleTarget
): void => {
  let localeBeforeFlow: string | null = null;
  const setLocale = (lang: string) => {
    if (i18n.locale !== lang) i18n.locale = lang;
  };

  router.afterEach(({ name }) => {
    const inFlow = !!name && PRE_AUTH_ROUTES.indexOf(name) !== -1;

    if (inFlow) {
      if (localeBeforeFlow === null) localeBeforeFlow = i18n.locale;
      if (!getBusinessLang()) setLocale(getRequestLang());
      return;
    }

    if (localeBeforeFlow === null) return;
    setLocale(getBusinessLang() || localeBeforeFlow);
    localeBeforeFlow = null;
  });
};
